import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const TicketSubjectSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(80, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required"))
});

const TicketSubjectModal = ({ open, onClose, ticketSubjectId }) => {
  const classes = useStyles();
  const initialState = {
    name: "",
    isActive: true,
    countInDashboard: true
  };
  const [ticketSubject, setTicketSubject] = useState(initialState);

  useEffect(() => {
    (async () => {
      if (!ticketSubjectId) return;

      try {
        const { data } = await api.get(`/ticket-subjects/${ticketSubjectId}`);
        setTicketSubject(prevState => ({ ...prevState, ...data }));
      } catch (error) {
        toastError(error);
      }
    })();

    return () => {
      setTicketSubject(initialState);
    };
  }, [ticketSubjectId, open]);

  const handleClose = () => {
    onClose();
    setTicketSubject(initialState);
  };

  const handleSaveTicketSubject = async values => {
    try {
      if (ticketSubjectId) {
        await api.put(`/ticket-subjects/${ticketSubjectId}`, values);
      } else {
        await api.post("/ticket-subjects", values);
      }

      toast.success(i18n.t("ticketSubjectModal.success"));
      handleClose();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} scroll="paper" fullWidth maxWidth="sm">
        <DialogTitle>
          {ticketSubjectId
            ? i18n.t("ticketSubjectModal.title.edit", { id: ticketSubjectId })
            : i18n.t("ticketSubjectModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={ticketSubject}
          enableReinitialize
          validationSchema={TicketSubjectSchema}
          onSubmit={async (values, actions) => {
            await handleSaveTicketSubject(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("ticketSubjectModal.form.subject")}
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(values.isActive)}
                      onChange={event => setFieldValue("isActive", event.target.checked)}
                    />
                  }
                  label={i18n.t("ticketSubjectModal.form.active")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(values.countInDashboard)}
                      onChange={event =>
                        setFieldValue("countInDashboard", event.target.checked)
                      }
                    />
                  }
                  label={i18n.t("ticketSubjectModal.form.countInDashboard")}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("ticketSubjectModal.buttons.cancel")}
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                  {ticketSubjectId
                    ? i18n.t("ticketSubjectModal.buttons.okEdit")
                    : i18n.t("ticketSubjectModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TicketSubjectModal;
